/* eslint-disable no-param-reassign */
import { createStore } from 'vuex';

// eslint-disable-next-line import/no-cycle
import Utils from '../../lib/utils';
import LocalStorageConfig from '../../config/localStorage';
import LocalStorageUtils from '../../lib/localStorageUtils';
import { capi } from './client';

// store modules
import breadcrumbs from './modules/breadcrumbs';
import chat from './modules/chat';
import results from './modules/results';
import systems from './modules/systems';
import translatableContent from './modules/translatable-content';
import email from './modules/email';
import faq from './modules/faq';
import url from './modules/url';
// eslint-disable-next-line import/no-cycle
import product from './modules/product';
import backlink from './modules/backlink';
import repair from './modules/repair';

const store = createStore({
  state: {
    apiCallsInProgress: [],
    commonTopics: [],
    isInternal: false,
    locale: window?.AppData?.locale,
    noProductFlowFlag: false,
    breadCrumbsList: [],
    topics: [],
    translations: window?.AppData?.translations,
    keywordSearchTerm: false,
    isTopicSelectedFlag: false,
    isSearchQuerySubmittedFlag: false,
    unavailableStateFlag: false,
    selectedTab: 'topics',
    routedComponentToRender: 'topics',
    prevFaq: false,
    backLinkResults: false,
    topicText: false,
    tabTopicsId: 'topics',
    bypassGAWrapperFlag: false,
    manuals: false,
    internationalManuals: false,
    softwareFaqId: false,
    isQuickLinkClickedFlag: false,
    isWelcomeTabVisible: false,
    hasSearchedFlag: LocalStorageUtils.isHasSearchedFlagSet(),
    hasSearchedFlagExpired: LocalStorageUtils.hasSearchedFlagExpired(),
    lastSearchedTimestamp: false,
    lastPageviewUrl: window?.AppData?.landingUrl || false,
    countries: [],
  },
  getters: {
    locale: (state) => state.locale,
    lastSearchedTimestamp: (state) => state.lastSearchedTimestamp,
    isWelcomeTabVisible: (state) => state.isWelcomeTabVisible,
    softwareFaqId: (state) => state.softwareFaqId,
    manuals: (state) => state.manuals,
    internationalManuals: (state) => state.internationalManuals,
    canUserSearch: () => (params) => {
      if (
        (typeof params.searchQuery !== 'undefined' && params.searchQuery && params.searchQuery.length)
        || (typeof params.topicTag !== 'undefined' && params.topicTag && params.topicTag.length)) {
        return true;
      }
      return false;
    },
    backLinkResults: (state) => state.backLinkResults,
    bypassGAWrapperFlag: (state) => state.bypassGAWrapperFlag,
    commonTopics: (state) => state.commonTopics,
    topicText: (state) => state.topicText,
    prevFaq: (state) => state.prevFaq,
    selectedTab: (state) => state.selectedTab,
    isLoading: (state) => state.apiCallsInProgress.length > 0,
    noProductFlowFlag: (state) => state.noProductFlowFlag,
    routedComponentToRender: (state) => state.routedComponentToRender,
    topics: (state) => state.topics,
    translations: (state) => state.translations,
    keywordSearchTerm: (state) => state.keywordSearchTerm,
    isTopicSelectedFlag: (state) => state.isTopicSelectedFlag,
    isSearchQuerySubmittedFlag: (state) => state.isSearchQuerySubmittedFlag,
    textResultUrl: (state, getters) => (result) => {
      if (getters.isAuthoredResult(result)) {
        return `?faq=${result.contentId}`;
      }
      return result.url;
    },
    isAuthoredResult: () => (result) => result.contentSource === 'Authored',
    tabTopicsId: (state) => state.tabTopicsId,
    isInternal: (state) => state.isInternal,
    canUserIdentifyProduct: () => (params) => {
      if (
        (typeof params.productID !== 'undefined' && params.productID && params.productID.length)
        || (typeof params.identifier !== 'undefined' && params.identifier && params.identifier.length)
        || (typeof params.partNumber !== 'undefined' && params.partNumber && params.partNumber.length)
      ) {
        return true;
      }
      return false;
    },
    getTopicTagFromId: (state, getters) => (tagId) => (getters.topics || [])
      .find((t) => t?.systemTagName === (tagId || getters.urlParams?.topicTag)),
    getGeneralTagNameFromId: (state, getters) => (tagId) => {
      if (typeof tagId === 'undefined') {
        tagId = getters.getURLParam('topicTag');
      }

      let tagName = false;

      if (tagId && state.topics.length > 0) {
        state.topics.forEach((topic) => {
          if (topic.systemTagName === tagId) {
            tagName = topic.displayTagName;
          }
        });
      }

      return tagName;
    },

    defaultPageTitle: (state) => state.translations.settings.scSeo.seoPageTitle,
    nonHomePageTitle: (state) => state.translations.settings.scSeo.seoNonHomepagePageTitle,

    getGroupedByLanguageKey: () => (internationalManualsList) => internationalManualsList.reduce(
      (arry, manual) => {
        arry[manual.language] = (arry[manual.language] || []).concat(manual);
        return arry;
      },
      {},
    ),

    isFullWidthTab: (state) => {
      let result = false;

      if (state.routedComponentToRender === 'psc-cases') {
        result = true;
      }

      return result;
    },

    isQuickLinkClickedFlag: (state) => state.isQuickLinkClickedFlag,

    hasSearchedFlag: (state) => {
      try {
        let hasSearchedFlag = false;

        if (localStorage) {
          if (state.hasSearchedFlag) {
            return state.hasSearchedFlag;
          }
          hasSearchedFlag = localStorage.getItem(LocalStorageConfig.hasSearchedFlag);

          if (hasSearchedFlag === 'true') {
            hasSearchedFlag = true;
          } else {
            hasSearchedFlag = false;
          }
        }

        return hasSearchedFlag;
      } catch (e) {
        return false;
      }
    },

    hasSearchedFlagExpired: (state) => {
      let hasSearchedFlagExpired = false;
      const lastSearchedTimestamp = parseInt(state.lastSearchedTimestamp, 10) || 0;
      let hasSearchedFlagDate = false;

      if (localStorage) {
        hasSearchedFlagDate = localStorage.getItem(LocalStorageConfig.lastSearchedTimestamp);
      }

      if (hasSearchedFlagDate) {
        hasSearchedFlagDate = parseInt(hasSearchedFlagDate, 10);

        if (lastSearchedTimestamp > hasSearchedFlagDate) {
          hasSearchedFlagDate = lastSearchedTimestamp;
        }

        const hasSearchedFlagExpirationDate = hasSearchedFlagDate + (LocalStorageConfig.searchFlagExpiration * 1000 * 60);

        if (hasSearchedFlagDate && hasSearchedFlagExpirationDate < Date.now()) {
          hasSearchedFlagExpired = true;
        }
      }

      return hasSearchedFlagExpired;
    },

    /**
     * Determines whether or not the email is disabled for the current product
     * @returns {boolean} indicating whether or not the email is disabled
     */
    isEmailDisabledForProduct: (state, getters) => {
      if (!state.noProductFlowFlag) {
        const { disableEmail } = getters.product;
        if (typeof disableEmail !== 'undefined' && disableEmail) {
          return true;
        }
      }
      return false;
    },

    unavailableStateFlag: (state) => state.unavailableStateFlag,

    isApiIdCallInProgress: (state) => (apiId) => state.apiCallsInProgress.filter((id) => id === apiId).length > 0,

    recaptchaSuccess: (state) => state.recaptchaSuccess,

    renderHome: (state, getters) => (
      !getters.product
      && (
        !getters.productFilteredList.length
        || (
          getters.productFilteredList.length
          && !getters.urlParams.productSearch
        )
      )
      && !getters.canUserIdentifyProduct(getters.urlParams)
      && !state.noProductFlowFlag
      && !state.isQuickLinkClickedFlag
      && !state.unavailableStateFlag
    ),

    lastPageviewUrl: (state) => state.lastPageviewUrl,

    /**
     * Determines whather or not the chat is supported by a product
     * @returns {boolean}
     */
    isProductChatSupported: (state, getters) => (
      getters.product.chatIdentifier
      && getters.product.chatIdentifier.length > 0
      && getters.product.chatQueueName
      && getters.product.chatQueueName.length > 0
    ),

    countries: (state) => state.countries,
  },

  mutations: {
    // eslint-disable-next-line no-shadow
    lastPageviewUrlUpdate(state, url) {
      state.lastPageviewUrl = url;
    },

    isWelcomeTabVisibleUpdate(state, bool) {
      state.isWelcomeTabVisible = bool;
    },
    softwareFaqIdUpdate(state, id) {
      state.softwareFaqId = id;
    },

    bypassGAWrapperFlagUpdate(state, flag) {
      state.bypassGAWrapperFlag = flag;
    },

    internationalManualsUpdate(state, internationalManuals) {
      state.internationalManuals = internationalManuals;
    },

    manualsUpdate(state, manuals) {
      state.manuals = manuals;
    },

    backLinkResultsUpdate(state, prevItem) {
      state.backLinkResults = prevItem;
    },

    commonTopicsUpdate(state, topics) {
      state.commonTopics = topics;
    },

    topicTextUpdate(state, text) {
      state.topicTextUpdate = text;
    },

    incrementApiCallsInProgress(state, apiId) {
      state.apiCallsInProgress.push(apiId);
    },

    decrementApiCallsInProgress(state, apiId) {
      state.apiCallsInProgress = state.apiCallsInProgress.filter((val) => val !== apiId);
    },

    noProductFlowFlagUpdate(state, flag) {
      state.noProductFlowFlag = flag;
      state.chat.chatAvailability = false;
    },

    selectedTabUpdate(state, selectedTab) {
      state.selectedTab = selectedTab;
    },

    topicsUpdate(state, topics) {
      state.topics = topics;
    },

    translationsUpdate(state, translations) {
      state.translations = translations;
    },

    keywordSearchTermUpdate(state, term) {
      state.keywordSearchTerm = term;
    },

    isTopicSelectedFlagUpdate(state, flag) {
      state.isTopicSelectedFlag = flag;
    },

    isSearchQuerySubmittedFlagUpdate(state, flag) {
      state.isSearchQuerySubmittedFlag = flag;
    },

    routedComponentToRenderUpdate(state, componentName) {
      state.routedComponentToRender = componentName;
    },

    prevFaqUpdate(state, prevFaq) {
      state.prevFaq = prevFaq;
    },

    tabTopicsIdUpdate(state, tabTopicsId) {
      state.tabTopicsId = tabTopicsId;
    },

    isInternalUpdate(state, value) {
      state.isInternal = value;
    },

    isQuickLinkClickedFlagUpdate(state, isQuickLinkClickedFlag) {
      state.isQuickLinkClickedFlag = isQuickLinkClickedFlag;
    },

    isHasSearchedFlagUpdate(state, flag) {
      state.hasSearchedFlag = flag;
      LocalStorageUtils.setLocalStorageValue(LocalStorageConfig.hasSearchedFlag, flag);
    },

    lastSearchedTimestampUpdate(state, timestamp) {
      state.lastSearchedTimestamp = timestamp;
      LocalStorageUtils.setLocalStorageValue(LocalStorageConfig.lastSearchedTimestamp, Date.now());
    },

    unavailableStateFlagUpdate(state, value) {
      state.unavailableStateFlag = value;
    },

    countriesUpdate(state, countries) {
      state.countries = countries;
    },
  },

  actions: {
    async fetchManuals({ commit, getters }, props) {
      const apiId = 'fetchManuals';
      if (getters.isApiIdCallInProgress(apiId)) return;

      const params = { ...getters.urlParams };
      const locale = params.altLocale || getters.locale;
      const qParams = { locale, productID: params.productID };
      const allDocsPartNumber = '010-DECNF-00';
      const cpscPartNumber = '010-0CPSC-00';
      qParams.productVersionKey = props.version;

      if (props.isDoc) qParams.partNumber = allDocsPartNumber;
      else if (props.isCpsc) qParams.partNumber = cpscPartNumber;
      else qParams.partNumber = getters.product?.partNumber || getters.product?.productManualsPrimarySku;

      if (!qParams.partNumber) return;
      commit('incrementApiCallsInProgress', apiId);

      try {
        const manualsReq = capi.get(`manuals/${locale}`, { params: qParams }).catch((e) => e);
        const internationalManualsReq = capi.get('manuals', { params: qParams }).catch((e) => e);
        const [{ data: manuals }, { data: internationalManuals }] = await Promise.all([manualsReq, internationalManualsReq]);

        if (manuals?.length) {
          if (props.isDoc || props.isCpsc) manuals.sort((a, b) => a.displayName.localeCompare(b.displayName));
          commit('manualsUpdate', manuals);
        } else commit('manualsUpdate', false);

        if (internationalManuals?.length) commit('internationalManualsUpdate', getters.getGroupedByLanguageKey(internationalManuals));
        else commit('internationalManualsUpdate', false);
      } catch (e) { /** */ } finally {
        commit('decrementApiCallsInProgress', apiId);
      }
    },

    async fetchSoftwareFaqId({ getters, commit }, softwareFaqTag) {
      const apiId = 'fetchSoftwareFaqId';
      if (getters.isApiIdCallInProgress(apiId)) return;
      commit('incrementApiCallsInProgress', apiId);

      const seriesTag = 'region_softwareseries';
      const productTag = 'region_softwareproduct';
      const params = Utils.getObjectCopy(getters.urlParams);

      softwareFaqTag = softwareFaqTag === 'series' ? seriesTag : productTag;
      params.productTagName = getters.product.tagName;
      params.topicTag = softwareFaqTag;

      try {
        const locale = params.altLocale || getters.locale;
        const endpointPath = `content/${locale}/?${getters['results/getTextResultsUrlQs'](params)}`;
        const { data } = await capi.get(endpointPath);

        if ((data.contentDetails || []).length === 1) commit('softwareFaqIdUpdate', data.contentDetails[0].contentId);
        else commit('softwareFaqIdUpdate', false);
      } catch (e) {
        commit('softwareFaqIdUpdate', false);
      } finally {
        commit('decrementApiCallsInProgress', apiId);
      }
    },

    handleBackLinkNoProductFlow({ commit, getters }) {
      if (getters.noProductFlowFlag) {
        const replaceUrl = window.location.href.split('?')[0];
        commit('results/textResultsUpdate', false);
        commit('results/videoResultsUpdate', false);
        commit('results/textResultsPrivateUpdate', false);
        window.history.replaceState({ ...getters.urlParams, current: '' }, null, replaceUrl);
      }
    },

    async fetchCommonTopics({ commit, getters }, params = {}) {
      const apiId = 'fetchCommonTopics';
      if (getters.isApiIdCallInProgress(apiId)) return;
      commit('incrementApiCallsInProgress', apiId);

      if (getters.topics.length === 0) {
        try {
          const { data } = await capi.get(`topics/byCategory/?locale=${getters.locale}&categoryTagName=topcat_noproduct`);
          commit('commonTopicsUpdate', data);

          if (params.assignToTopics) {
            commit('topicsUpdate', data);
          }
        } catch (e) { /** */ } finally {
          commit('decrementApiCallsInProgress', apiId);
        }
      } else if (params.assignToTopics) {
        commit('topicsUpdate', getters.commonTopics);
      }
    },

    // Reset all URL parameters and data
    resetState({ commit, dispatch }) {
      commit('productUpdate', false);
      commit('productDetailsFaqSearchUpdate', null);
      commit('topicsUpdate', []);
      commit('selectedTabUpdate', 'topics');
      commit('noProductFlowFlagUpdate', false);
      commit('isTopicSelectedFlagUpdate', false);
      commit('isSearchQuerySubmittedFlagUpdate', false);
      commit('isQuickLinkClickedFlagUpdate', false);
      commit('isWelcomeTabVisibleUpdate', false);
      dispatch('results/reset');
      dispatch('resetFaqModule');
      dispatch('resetUrlParams');
    },

    noProductFlowEnable({ commit, getters, dispatch }) {
      if (getters.urlParams.focus) {
        window.location = `${window.location.origin}/${window.AppData.locale}/?searchType=noProduct`;
      } else {
        dispatch('resetState');
        commit('noProductFlowFlagUpdate', true);
        dispatch('fetchCommonTopics', {
          assignToTopics: true,
        });
        commit('routedComponentToRenderUpdate', 'topics');
        window.history.pushState({ ...getters.urlParams }, null, window.location.href);
      }
    },

    async fetchCountries({ commit, getters }) {
      const apiId = 'fetchCountries';
      if (getters.isApiIdCallInProgress(apiId)) return;
      commit('incrementApiCallsInProgress', apiId);

      try {
        const { data } = await capi.get('/countries');
        commit('countriesUpdate', data);
      } catch (e) { /** */ } finally {
        commit('decrementApiCallsInProgress', apiId);
      }
    },

  },

  modules: {
    breadcrumbs,
    chat,
    results,
    systems,
    translatableContent,
    email,
    faq,
    product,
    url,
    backlink,
    repair,
  },
});

export default store;
